type AppSupabase = import('@supabase/supabase-js').SupabaseClient;

export type BookingCustomerRow = {
  id: string;
  customer_id: string | null;
  customer_repeat_status: string | null;
  end_date: string;
  created_at: string;
};

export type NewRepeatCounts = { newCount: number; repeatCount: number };

export type ClassifiedBooking = BookingCustomerRow & {
  isNew: boolean;
};

type HistoryBooking = { id: string; end_date: string; created_at: string };

export function countFromRepeatStatus(rows: BookingCustomerRow[]): {
  newCount: number;
  repeatCount: number;
  needsInference: BookingCustomerRow[];
} {
  let newCount = 0;
  let repeatCount = 0;
  const needsInference: BookingCustomerRow[] = [];

  for (const row of rows) {
    if (row.customer_repeat_status === 'new') {
      newCount += 1;
    } else if (row.customer_repeat_status === 'repeat') {
      repeatCount += 1;
    } else {
      needsInference.push(row);
    }
  }

  return { newCount, repeatCount, needsInference };
}

export function buildHistoryByCustomer(
  bookings: Array<{ id: string; customer_id: string | null; end_date: string; created_at: string }>
): Map<string, HistoryBooking[]> {
  const historyByCustomer = new Map<string, HistoryBooking[]>();

  for (const booking of bookings) {
    if (!booking.customer_id) continue;
    const listForCustomer = historyByCustomer.get(booking.customer_id) ?? [];
    listForCustomer.push({
      id: booking.id,
      end_date: booking.end_date,
      created_at: booking.created_at,
    });
    historyByCustomer.set(booking.customer_id, listForCustomer);
  }

  for (const history of historyByCustomer.values()) {
    history.sort((a, b) => {
      const byEndDate = a.end_date.localeCompare(b.end_date);
      return byEndDate !== 0 ? byEndDate : a.created_at.localeCompare(b.created_at);
    });
  }

  return historyByCustomer;
}

export function classifyBookingsNewRepeat(
  rows: BookingCustomerRow[],
  historyByCustomer: Map<string, HistoryBooking[]>
): ClassifiedBooking[] {
  return rows.map((row) => {
    if (row.customer_repeat_status === 'new') {
      return { ...row, isNew: true };
    }
    if (row.customer_repeat_status === 'repeat') {
      return { ...row, isNew: false };
    }

    if (!row.customer_id) {
      return { ...row, isNew: true };
    }

    const history = historyByCustomer.get(row.customer_id);
    if (!history?.length) {
      return { ...row, isNew: true };
    }

    const idx = history.findIndex((booking) => booking.id === row.id);
    return { ...row, isNew: idx <= 0 };
  });
}

export function sumNewRepeatCounts(rows: ClassifiedBooking[]): NewRepeatCounts {
  let newCount = 0;
  let repeatCount = 0;
  for (const row of rows) {
    if (row.isNew) newCount += 1;
    else repeatCount += 1;
  }
  return { newCount, repeatCount };
}

type BookingFilters = (
  query: ReturnType<AppSupabase['from']>
) => ReturnType<ReturnType<AppSupabase['from']>['select']>;

export async function countNewRepeatBookings(
  rows: BookingCustomerRow[] | null,
  supabase: AppSupabase,
  bookingFilters: BookingFilters
): Promise<NewRepeatCounts> {
  const list = rows ?? [];
  if (list.length === 0) {
    return { newCount: 0, repeatCount: 0 };
  }

  const classified = await classifyBookingsWithHistory(list, supabase, bookingFilters);
  return sumNewRepeatCounts(classified);
}

export async function classifyBookingsWithHistory(
  rows: BookingCustomerRow[],
  supabase: AppSupabase,
  bookingFilters: BookingFilters
): Promise<ClassifiedBooking[]> {
  if (rows.length === 0) return [];

  const { needsInference } = countFromRepeatStatus(rows);
  if (needsInference.length === 0) {
    return classifyBookingsNewRepeat(rows, new Map());
  }

  const customerIds = [
    ...new Set(
      needsInference.map((row) => row.customer_id).filter((id): id is string => Boolean(id))
    ),
  ];

  if (customerIds.length === 0) {
    return classifyBookingsNewRepeat(rows, new Map());
  }

  const { data, error } = await bookingFilters(
    supabase.from('bookings').select('id, customer_id, end_date, created_at')
  ).in('customer_id', customerIds);

  if (error) {
    console.error('Error inferring new vs repeat bookings:', error);
    return classifyBookingsNewRepeat(rows, new Map());
  }

  const historyByCustomer = buildHistoryByCustomer(data ?? []);
  return classifyBookingsNewRepeat(rows, historyByCustomer);
}
